import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, NavBar, Toast } from 'antd-mobile';
import { t } from '../../i18n';
import { exchangeSupportLoginShortCode } from './graphql/exchangeSupportLoginShortCode';
import {useHandleLoginSuccess, UserInfo, AppRole} from '../../utils/auth';
import {DEVICE_ID_KEY} from "../../utils/deviceUtils";

const SupportLoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const handleLoginSuccess = useHandleLoginSuccess();

    const onFinish = async (values: { phone: string; shortCode: string }) => {
        if (!values.phone || values.phone.trim() === '') {
            Toast.show(t('supportTools.supportLogin.phoneRequired'));
            return;
        }
        if (!values.shortCode || values.shortCode.trim() === '') {
            Toast.show(t('supportTools.supportLogin.shortCodeRequired'));
            return;
        }

        const phone = parseInt(values.phone.trim(), 10);
        if (isNaN(phone)) {
            Toast.show(t('supportTools.supportLogin.invalidPhone'));
            return;
        }

        try {
            setIsLoading(true);
            const result = await exchangeSupportLoginShortCode(phone, values.shortCode.trim());
            if (result?.success && result?.token && result?.account) {
                const userInfo: UserInfo = {
                    token: result.token,
                    account: {
                        id: result.account.id,
                        name: result.account.name,
                        phone: result.account.phone,
                        avatarUrl: result.account.avatarUrl,
                        role: {
                            name: result.account.role.name as AppRole,
                            permissions: result.account.role.permissions,
                        },
                    },
                };
                await handleLoginSuccess(userInfo);
                localStorage.setItem(DEVICE_ID_KEY, result.deviceId!)
                Toast.show(t('login.success'));
                setTimeout(() => {
                    navigate('/');
                }, 1500);
            } else {
                const messageKey = result?.message;
                Toast.show(messageKey ? t(messageKey) : t('supportTools.supportLogin.loginFailed'));
                setIsLoading(false);
            }
        } catch (error: any) {
            Toast.show(t('supportTools.supportLogin.loginFailed'));
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <NavBar onBack={() => navigate(-1)}>
                {t('supportTools.supportLogin.title')}
            </NavBar>

            <div className="flex flex-col p-4 gap-6">
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    style={{
                        '--border-top': 'none',
                        '--border-inner': 'none',
                        '--border-bottom': 'none',
                    }}
                    footer={
                        <Button
                            block
                            color="primary"
                            size="large"
                            type="submit"
                            loading={isLoading}
                        >
                            {t('pages.login.logIn')}
                        </Button>
                    }
                >
                    <Form.Item name="phone" className="mb-4">
                        <Input
                            clearable
                            placeholder={t('supportTools.supportLogin.phonePlaceholder')}
                            type="tel"
                            inputMode="numeric"
                        />
                    </Form.Item>

                    <Form.Item name="shortCode" className="mb-4">
                        <Input
                            clearable
                            placeholder={t('supportTools.supportLogin.shortCodePlaceholder')}
                            type="tel"
                            inputMode="numeric"
                        />
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default SupportLoginPage;
